import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { KSelectAutocomplete, KTextInput } from "@kyburz/kdesign-system";
import { makeStyles } from "@material-ui/core/styles";
import useSetState from "../../hooks/useSetState";
import BaseLayout from "../../components/layouts/BaseLayout";
import KFormBackground from "../../components/ui/KFormBackground";
import { setAlert } from "../../redux/actions/actions";
import { createIoTKeyDevice } from "../../apiCalls/config/iotKeyDevices";
import { getCustomers } from "../../apiCalls/auth";
import { BLE_DEVICE_TYPES } from "./IoTKeyDeviceView";
import {
  formatErrorResponse,
  validateMacAddress,
} from "../../utils/stringUtils";

const useStyles = makeStyles(() => ({
  field: {
    marginRight: 24,
  },
  block: {
    display: "flex",
    marginBottom: 16,
  },
}));

const NewIoTKeyDevice: React.FC = () => {
  const history: any = useHistory();
  const classes = useStyles();

  const dispatch = useDispatch();

  const [
    {
      company_id,
      iot_key_id,
      ble_device_type,
      mac_address,
      firmware_version,
      hardware_version,
      formControlCustomers,
    },
    setState,
  ] = useSetState({
    company_id: "",
    iot_key_id: "",
    ble_device_type: null as null | string,
    mac_address: null as null | string,
    firmware_version: "",
    hardware_version: "",
    formControlCustomers: [],
  });

  const isMacAddressValid = !mac_address || validateMacAddress(mac_address);

  useEffect(() => {
    getCustomers().then((res) => {
      setState({
        formControlCustomers: res.data
          .filter((el) => el._id !== null)
          .map((el) => ({ value: el.shortname, label: el.name })),
      });
    });
  }, []);

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      await createIoTKeyDevice(
        company_id,
        iot_key_id,
        ble_device_type,
        mac_address,
        firmware_version,
        hardware_version,
      );
      history.push("/config/iot_key_devices/devices");
      dispatch(setAlert("IoT key device created", "success"));
    } catch (error) {
      dispatch(setAlert(formatErrorResponse(error), "error"));
    }
  };

  const handleChange = (e) => setState({ [e.target.name]: e.target.value });
  const handleSelectAutoComplete = (name, value) => {
    setState({ [name]: value?.value || value?.label });
  };

  return (
    <BaseLayout
      title="Create New IoT Key Device"
      handleArrowClick={() => history.goBack()}
    >
      <KFormBackground
        size="large"
        primaryCTAColor="primary"
        primaryCTAonClick={handleSubmit}
        primaryCTAText="Save"
        secondaryCTAText="Cancel"
        secondaryCTAonClick={() => history.goBack()}
        disabled={
          iot_key_id.length === 0 ||
          company_id.length === 0 ||
          !isMacAddressValid
        }
      >
        <div className={classes.block}>
          <KTextInput
            label="IoT Key Id"
            name="iot_key_id"
            value={iot_key_id}
            onChange={handleChange}
            placeholder="b255bbef-da4b-4d76-8752-8b75dbae2a0e"
            required
            rootClassName={classes.field}
          />
        </div>
        <div className={classes.block}>
          <KSelectAutocomplete
            label="Company"
            name="company_id"
            value={company_id}
            onChange={handleSelectAutoComplete}
            elements={formControlCustomers}
            placeholder="Company"
            required
          />
        </div>
        <div className={classes.block}>
          <KSelectAutocomplete
            label="BLE device type"
            name="ble_device_type"
            value={ble_device_type}
            onChange={handleSelectAutoComplete}
            elements={BLE_DEVICE_TYPES}
            placeholder="BLE device type"
            key="ble_device_type"
            isClearable
          />
        </div>
        <div className={classes.block}>
          <KTextInput
            label="MAC address"
            name="mac_address"
            value={mac_address}
            rootClassName={classes.field}
            onChange={handleChange}
            placeholder="1A:2B:3C:4D:5E:6F"
            errorText="MAC address should have format '1A:2B:3C:4D:5E:6F'"
            error={!isMacAddressValid}
            required={false}
            key="mac_address"
          />
        </div>
        <div className={classes.block}>
          <KTextInput
            label="Firmware Version"
            name="firmware_version"
            value={firmware_version}
            onChange={handleChange}
            placeholder="Firmware version"
            required={false}
            rootClassName={classes.field}
          />
          <KTextInput
            label="Hardware Version"
            name="hardware_version"
            value={hardware_version}
            onChange={handleChange}
            placeholder="Hardware version"
            required={false}
            rootClassName={classes.field}
          />
        </div>
      </KFormBackground>
    </BaseLayout>
  );
};

export default NewIoTKeyDevice;
